import { FC, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { listGiftBrandSellerDeliveries } from '@shared/api/giftDelivery/giftDelivery.controller';
import {
  GiftDeliveryForAdmin,
  GiftDeliveryStatus,
} from '@shared/api/giftDelivery/giftDelivery.interface';
import { giftItemBrandListDataAtom } from '@shared/api/giftItemBrand/giftItemBrand.atom';
import { useGiftItemBrandListHook } from '@shared/api/giftItemBrand/giftItemBrand.hook';
import { Loading } from '@shared/components/Loading';
import { LowerRole } from '@shared/types';
import { useAsyncFn } from 'react-use';

import { GiftBrandThanksCardModal } from '../components/GiftBrandThanksCardModal';
import { statusLabel, typeLabel } from '../../giftSeller/utils/delivery.constants';
import {
  formatDate,
  formatPhone,
  getItemName,
  getOrdererInfo,
} from '../../giftSeller/utils/delivery.utils';

// 과거 배송검색: 날짜 상관없이 수령인/연락처/상태/기간으로 검색 (읽기 전용, CS 대응용)
export const GiftBrandDeliverySearchPage: FC =
  function GiftBrandDeliverySearchPage() {
    const [recipientName, setRecipientName] = useState('');
    const [recipientPhone, setRecipientPhone] = useState('');
    const [status, setStatus] = useState<GiftDeliveryStatus | 'all'>('all');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [cardDelivery, setCardDelivery] = useState<GiftDeliveryForAdmin | null>(
      null,
    );

    // 감사카드 템플릿 (취소·완료 포함 모든 건 카드 조회용)
    useGiftItemBrandListHook(LowerRole.GiftBrandSeller);
    const brands = useRecoilValue(giftItemBrandListDataAtom);
    const thanksTemplates = useMemo(
      () => brands?.[0]?.thanksPrint?.templates ?? [],
      [brands],
    );

    const [{ loading, value: results }, search] = useAsyncFn(async () => {
      const filter: Record<string, unknown> = {};
      if (status !== 'all') filter.deliveryStatus = status;
      if (recipientName.trim())
        filter['recipient.name'] = { $regex: recipientName.trim() };
      if (recipientPhone.trim())
        filter['recipient.phoneNumber'] = {
          $regex: recipientPhone.trim().replace(/-/g, ''),
        };
      if (dateFrom || dateTo) {
        const range: Record<string, string> = {};
        if (dateFrom) range.$gte = new Date(dateFrom).toISOString();
        if (dateTo) {
          const to = new Date(dateTo);
          to.setHours(23, 59, 59, 999);
          range.$lte = to.toISOString();
        }
        filter.createdAt = range;
      }
      return listGiftBrandSellerDeliveries(filter);
    }, [recipientName, recipientPhone, status, dateFrom, dateTo]);

    const rows = results ?? [];

    return (
      <div>
        <div className="mb-4">
          <h2 className="text-lg font-bold">과거 배송검색</h2>
          <p className="mt-1 text-sm text-gray-500">
            날짜와 상관없이 지난 배송 내역을 검색해 문의에 응대할 수 있습니다.
          </p>
        </div>

        {/* 검색 폼 */}
        <div className="mb-4 rounded-lg bg-white p-4 shadow">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-600">
                수령인
              </label>
              <input
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder="수령인 이름"
                className="w-full rounded border px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-600">
                연락처
              </label>
              <input
                value={recipientPhone}
                onChange={(e) => setRecipientPhone(e.target.value)}
                placeholder="전화번호"
                className="w-full rounded border px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-600">
                상태
              </label>
              <select
                value={status}
                onChange={(e) =>
                  setStatus(e.target.value as GiftDeliveryStatus | 'all')
                }
                className="w-full rounded border px-3 py-2 text-sm"
              >
                <option value="all">전체</option>
                {Object.values(GiftDeliveryStatus).map((s) => (
                  <option key={s} value={s}>
                    {statusLabel[s]?.text ?? s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-600">
                구매일 시작
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full rounded border px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-600">
                구매일 종료
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full rounded border px-3 py-2 text-sm"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => search()}
                disabled={loading}
                className="w-full rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {loading ? '검색중...' : '검색'}
              </button>
            </div>
          </div>
        </div>

        {/* 결과 */}
        {loading ? (
          <div className="center-box py-16">
            <Loading />
          </div>
        ) : results === undefined ? (
          <div className="rounded-lg bg-white p-8 text-center text-gray-400 shadow">
            검색 조건을 입력하고 검색하세요.
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">
            검색 결과가 없습니다.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg bg-white shadow">
            <div className="border-b px-3 py-2 text-xs text-gray-500">
              총 {rows.length}건
            </div>
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">상태</th>
                  <th className="px-3 py-2 text-left">코드</th>
                  <th className="px-3 py-2 text-left">상품</th>
                  <th className="px-3 py-2 text-left">수량</th>
                  <th className="px-3 py-2 text-left">수령인</th>
                  <th className="px-3 py-2 text-left">배송지</th>
                  <th className="px-3 py-2 text-left">송장</th>
                  <th className="px-3 py-2 text-left">주문자</th>
                  <th className="px-3 py-2 text-left">구매일</th>
                  <th className="px-3 py-2 text-left">감사카드</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((d) => {
                  const orderer = getOrdererInfo(d);
                  const badge = statusLabel[d.deliveryStatus] ?? {
                    text: d.deliveryStatus,
                    className: 'bg-gray-100 text-gray-600',
                  };
                  return (
                    <tr key={d._id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-3 py-3">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${badge.className}`}
                        >
                          {badge.text}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 font-mono text-xs text-gray-500">
                        {d.fullCode || d.code || '-'}
                      </td>
                      <td className="px-3 py-3">
                        <p className="max-w-[160px] truncate font-medium">
                          {getItemName(d)}
                        </p>
                        <span className="text-[10px] text-gray-400">
                          {typeLabel[d.type] || d.type}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3">{d.quantity}개</td>
                      <td className="whitespace-nowrap px-3 py-3">
                        <p className="font-medium">{d.recipient?.name || '-'}</p>
                        <p className="text-xs text-gray-500">
                          {formatPhone(d.recipient?.phoneNumber)}
                        </p>
                      </td>
                      <td className="max-w-[200px] px-3 py-3">
                        {d.recipient?.address ? (
                          <p className="truncate text-xs text-gray-600">
                            ({d.recipient.postalCode}) {d.recipient.address}{' '}
                            {d.recipient.addressDetail || ''}
                          </p>
                        ) : (
                          <span className="text-xs text-gray-400">미입력</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-600">
                        {d.shipping?.trackingNumber ? (
                          <>
                            <p>{d.shipping.carrier || '-'}</p>
                            <p className="font-mono">{d.shipping.trackingNumber}</p>
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3">
                        <p>{orderer.name}</p>
                        <p className="text-xs text-gray-500">
                          {formatPhone(orderer.phone)}
                        </p>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                        {formatDate(orderer.purchasedAt)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3">
                        {d.thanksPrint ? (
                          <button
                            onClick={() => setCardDelivery(d)}
                            className="rounded border border-rose-300 px-3 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50"
                          >
                            감사카드
                          </button>
                        ) : (
                          <span className="text-xs text-gray-300">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* 감사카드 미리보기 / 인쇄 */}
        <GiftBrandThanksCardModal
          delivery={cardDelivery}
          templates={thanksTemplates}
          onClose={() => setCardDelivery(null)}
        />
      </div>
    );
  };
